import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { 
  ChevronLeft, ChevronRight, Home, Loader2, Play, Pause, 
  Volume2, VolumeX, SkipBack, SkipForward, Headphones, BookOpen
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOOK_COVERS = {
  1: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/bd6e58467b0f72e40f4d6f7b4e8e00482aed1e586249fa3ee56caf893946c8b7.png",
  2: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/23a2fb43e43abe556a66e843fe051696509f2bb93373f07870180babf5898d77.png",
  3: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/5668d8a988530b00b8956d1195a6b035691a54f8794ca67dc2b24681479edec6.png"
};

function formatTime(seconds) {
  if (isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// --- Extracted Components ---

function BookCoverPanel({ book, bookNumber, generatingAudio, generationProgress, onGenerateAll }) {
  return (
    <div className="space-y-4">
      <div className="rounded-none border-2 border-signal-red bg-[#0a0a0a] p-3">
        <div className="aspect-[2/3] relative overflow-hidden">
          <img src={BOOK_COVERS[parseInt(bookNumber)]} alt={book.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="font-['Syne'] text-lg font-bold text-[#e5e5e5]">{book.title}</div>
            <div className="font-mono text-xs text-[#a3a3a3]">Book {book.book_number} of 3</div>
          </div>
        </div>
      </div>
      <button
        onClick={onGenerateAll}
        disabled={generatingAudio}
        className="w-full rounded-none border-2 border-terminal-green bg-transparent text-terminal-green hover:bg-terminal-green hover:text-obsidian brutal-btn font-mono uppercase tracking-wide px-4 py-3 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="generate-all-audio-btn"
      >
        {generatingAudio ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Generating... {generationProgress > 0 ? `${generationProgress}%` : ''}</>
        ) : (
          <><Headphones className="w-4 h-4" /> Generate All Audio</>
        )}
      </button>
    </div>
  );
}

function AudioControls({ audioRef, isPlaying, currentTime, duration, volume, isMuted, onTogglePlay, onSeek, onVolumeChange, onToggleMute, onSkipForward, onSkipBackward }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <input
          type="range" min="0" max="100"
          value={duration ? (currentTime / duration) * 100 : 0}
          onChange={onSeek}
          className="w-full h-2 bg-[#262626] rounded-none appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-signal-red [&::-webkit-slider-thumb]:cursor-pointer"
          data-testid="audio-progress"
        />
        <div className="flex justify-between font-mono text-xs text-[#525252]">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
      <div className="flex items-center justify-center gap-4">
        <button onClick={onSkipBackward} className="p-3 rounded-none border border-[#262626] text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green transition-colors" data-testid="skip-back-btn">
          <SkipBack className="w-5 h-5" />
        </button>
        <button onClick={onTogglePlay} className="p-4 rounded-none border-2 border-signal-red bg-signal-red/10 text-signal-red hover:bg-signal-red hover:text-obsidian transition-colors" data-testid="play-pause-btn">
          {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8" />}
        </button>
        <button onClick={onSkipForward} className="p-3 rounded-none border border-[#262626] text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green transition-colors" data-testid="skip-forward-btn">
          <SkipForward className="w-5 h-5" />
        </button>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={onToggleMute} className="text-[#a3a3a3] hover:text-[#e5e5e5]">
          {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <input
          type="range" min="0" max="100"
          value={isMuted ? 0 : volume * 100}
          onChange={onVolumeChange}
          className="w-24 h-1 bg-[#262626] rounded-none appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-[#a3a3a3] [&::-webkit-slider-thumb]:cursor-pointer"
          data-testid="volume-slider"
        />
      </div>
    </div>
  );
}

function GenerateChapterButton({ chapterId, generatingChapterId, generatingAudio, generationProgress, onGenerate }) {
  return (
    <div className="text-center py-8">
      <p className="text-[#a3a3a3] font-mono text-sm mb-4">Audio not generated for this chapter yet.</p>
      <button
        onClick={() => onGenerate(chapterId)}
        disabled={generatingAudio}
        className="rounded-none border-2 border-signal-red bg-transparent text-signal-red hover:bg-signal-red hover:text-obsidian brutal-btn font-mono uppercase tracking-wide px-6 py-3 text-sm font-bold disabled:opacity-50 flex items-center gap-2 mx-auto"
        data-testid="generate-chapter-audio-btn"
      >
        {generatingChapterId === chapterId ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Generating... {generationProgress > 0 ? `${generationProgress}%` : ''}</>
        ) : (
          <><Headphones className="w-4 h-4" /> Generate Audio for This Chapter</>
        )}
      </button>
    </div>
  );
}

function PlayerChapterList({ chapters, currentChapterIndex, isPlaying, onSelect }) {
  return (
    <div className="rounded-none border border-[#262626] bg-[#0a0a0a] p-4">
      <div className="font-mono text-xs uppercase tracking-widest text-[#525252] mb-3">All Chapters</div>
      <div className="space-y-2">
        {chapters.map((chapter, index) => (
          <button
            key={chapter.id}
            onClick={() => onSelect(index)}
            className={`w-full text-left rounded-none border p-3 brutal-btn transition-all flex items-center justify-between ${
              index === currentChapterIndex
                ? 'border-signal-red bg-signal-red/10 text-signal-red'
                : 'border-[#262626] bg-transparent text-[#a3a3a3] hover:border-[#404040] hover:text-[#e5e5e5]'
            }`}
            data-testid={`chapter-${index + 1}-btn`}
          >
            <div>
              <div className="font-mono text-xs opacity-60 mb-1">Chapter {chapter.chapter_number}</div>
              <div className="font-['Syne'] font-bold text-sm">{chapter.title}</div>
            </div>
            <div className="flex items-center gap-2">
              {chapter.audio_base64 ? (
                <span className="font-mono text-xs text-terminal-green">&#9679;</span>
              ) : (
                <span className="font-mono text-xs text-[#525252]">&#9675;</span>
              )}
              {index === currentChapterIndex && isPlaying && (
                <div className="flex items-center gap-1">
                  <div className="w-1 h-3 bg-signal-red animate-pulse"></div>
                  <div className="w-1 h-4 bg-signal-red animate-pulse" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-1 h-2 bg-signal-red animate-pulse" style={{animationDelay: '0.2s'}}></div>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Main Component ---

export default function PlayerPage() {
  const navigate = useNavigate();
  const { bookNumber } = useParams();
  const audioRef = useRef(null);
  const pollingRef = useRef(null);
  
  const [book, setBook] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [generatingChapterId, setGeneratingChapterId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const fetchBookData = useCallback(async () => {
    try {
      const [bookRes, chaptersRes] = await Promise.all([
        axios.get(`${API}/books/${bookNumber}`),
        axios.get(`${API}/books/${bookNumber}/chapters`)
      ]);
      setBook(bookRes.data);
      setChapters(chaptersRes.data);
      setLoading(false);
    } catch {
      toast.error("Failed to load book");
      setLoading(false);
    }
  }, [bookNumber]);

  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  useEffect(() => { fetchBookData(); }, [fetchBookData]);

  useEffect(() => {
    if (audioRef.current && chapters[currentChapterIndex]?.audio_base64) {
      audioRef.current.src = `data:audio/mpeg;base64,${chapters[currentChapterIndex].audio_base64}`;
      audioRef.current.load();
      setCurrentTime(0);
      setIsPlaying(false);
    }
  }, [currentChapterIndex, chapters]);

  const pollAudioStatus = useCallback((chapterId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/audio-status/${chapterId}`);
        const { status, progress } = res.data;
        setGenerationProgress(progress || 0);
        if (status === "complete") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGeneratingAudio(false);
          setGeneratingChapterId(null);
          setGenerationProgress(0);
          toast.dismiss();
          toast.success("Audio generated!");
          await fetchBookData();
        } else if (status === "error") {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGeneratingAudio(false);
          setGeneratingChapterId(null);
          setGenerationProgress(0);
          toast.dismiss();
          toast.error("Audio generation failed. Please try again.");
        }
      } catch {
        // Polling error — will retry on next interval
      }
    }, 3000);
  }, [fetchBookData]);

  const handleGenerateAudio = useCallback(async (chapterId) => {
    setGeneratingAudio(true);
    setGeneratingChapterId(chapterId);
    setGenerationProgress(0);
    try {
      toast.loading("Generating audio narration...");
      await axios.post(`${API}/generate-audio/${chapterId}`);
      pollAudioStatus(chapterId);
    } catch {
      toast.dismiss();
      toast.error("Failed to start audio generation.");
      setGeneratingAudio(false);
      setGeneratingChapterId(null);
    }
  }, [pollAudioStatus]);

  const handleGenerateAllAudio = useCallback(async () => {
    setGeneratingAudio(true);
    setGenerationProgress(0);
    try {
      toast.loading("Generating audio for all chapters...");
      const res = await axios.post(`${API}/generate-book-audio/${bookNumber}`);
      if (res.data.queued_chapters > 0) {
        const firstChapter = chapters.find(c => !c.audio_base64);
        if (firstChapter) {
          setGeneratingChapterId(firstChapter.id);
          pollAudioStatus(firstChapter.id);
        }
      } else {
        toast.dismiss();
        toast.success("All chapters already have audio!");
        setGeneratingAudio(false);
      }
    } catch {
      toast.dismiss();
      toast.error("Failed to start audio generation");
      setGeneratingAudio(false);
    }
  }, [bookNumber, chapters, pollAudioStatus]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * duration;
    if (audioRef.current) { audioRef.current.currentTime = seekTime; setCurrentTime(seekTime); }
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolume(newVolume);
    if (audioRef.current) { audioRef.current.volume = newVolume; }
    if (newVolume > 0) setIsMuted(false);
  };

  const toggleMute = () => {
    if (audioRef.current) { audioRef.current.muted = !isMuted; setIsMuted(!isMuted); }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (currentChapterIndex < chapters.length - 1) setCurrentChapterIndex(prev => prev + 1);
  };

  const skipForward = () => { if (audioRef.current) audioRef.current.currentTime = Math.min(currentTime + 30, duration); };
  const skipBackward = () => { if (audioRef.current) audioRef.current.currentTime = Math.max(currentTime - 10, 0); };

  const currentChapter = chapters[currentChapterIndex];
  const hasAudio = currentChapter?.audio_base64;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian">
        <Loader2 className="w-12 h-12 animate-spin text-terminal-green" />
      </div>
    );
  }

  if (!book || chapters.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian">
        <div className="text-center space-y-4">
          <p className="text-signal-red font-mono">Book not found</p>
          <button onClick={() => navigate("/")} className="rounded-none border-2 border-terminal-green bg-transparent text-terminal-green hover:bg-terminal-green hover:text-obsidian brutal-btn font-mono uppercase tracking-wide px-6 py-3 text-sm font-bold">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grain-overlay" />
      <div className="scanlines" />
      <div className="vignette" />
      
      <audio
        ref={audioRef}
        onTimeUpdate={() => { if (audioRef.current) setCurrentTime(audioRef.current.currentTime); }}
        onLoadedMetadata={() => { if (audioRef.current) setDuration(audioRef.current.duration); }}
        onEnded={handleEnded}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="border-b border-[#262626] bg-[#0a0a0a]/90 backdrop-blur-sm sticky top-0 z-50 p-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/")} className="rounded-none border-2 border-[#262626] bg-transparent text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase tracking-wide px-3 py-2 text-xs font-bold flex items-center gap-2" data-testid="back-home-btn">
                <Home className="w-4 h-4" /> Home
              </button>
              <button onClick={() => navigate(`/reader/${bookNumber}`)} className="rounded-none border-2 border-[#262626] bg-transparent text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase tracking-wide px-3 py-2 text-xs font-bold flex items-center gap-2" data-testid="read-btn">
                <BookOpen className="w-4 h-4" /> Read
              </button>
            </div>
            <div className="font-mono text-xs uppercase tracking-widest text-signal-red signal-glow flex items-center gap-2">
              <Headphones className="w-4 h-4" /> Audiobook Mode
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-8">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8">
              <BookCoverPanel book={book} bookNumber={bookNumber} generatingAudio={generatingAudio} generationProgress={generationProgress} onGenerateAll={handleGenerateAllAudio} />
              
              <div className="space-y-6">
                {/* Now Playing */}
                <div className="rounded-none border-2 border-signal-red bg-[#0a0a0a] p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="font-mono text-xs uppercase tracking-widest text-[#525252] mb-1">Now Playing</div>
                      <h2 className="font-['Syne'] text-2xl font-bold text-signal-red signal-glow">
                        Chapter {currentChapter?.chapter_number}: {currentChapter?.title}
                      </h2>
                    </div>
                    {hasAudio && (
                      <div className="font-mono text-xs text-terminal-green terminal-glow px-2 py-1 border border-terminal-green">AUDIO READY</div>
                    )}
                  </div>
                  
                  {hasAudio ? (
                    <AudioControls
                      audioRef={audioRef} isPlaying={isPlaying} currentTime={currentTime} duration={duration}
                      volume={volume} isMuted={isMuted} onTogglePlay={togglePlay} onSeek={handleSeek}
                      onVolumeChange={handleVolumeChange} onToggleMute={toggleMute}
                      onSkipForward={skipForward} onSkipBackward={skipBackward}
                    />
                  ) : (
                    <GenerateChapterButton
                      chapterId={currentChapter?.id} generatingChapterId={generatingChapterId}
                      generatingAudio={generatingAudio} generationProgress={generationProgress}
                      onGenerate={handleGenerateAudio}
                    />
                  )}
                </div>
                
                <PlayerChapterList chapters={chapters} currentChapterIndex={currentChapterIndex} isPlaying={isPlaying} onSelect={setCurrentChapterIndex} />
                
                {/* Chapter Navigation */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setCurrentChapterIndex(Math.max(0, currentChapterIndex - 1))}
                    disabled={currentChapterIndex === 0}
                    className="flex-1 rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs py-3 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous Chapter
                  </button>
                  <button
                    onClick={() => setCurrentChapterIndex(Math.min(chapters.length - 1, currentChapterIndex + 1))}
                    disabled={currentChapterIndex === chapters.length - 1}
                    className="flex-1 rounded-none border-2 border-[#262626] bg-transparent text-[#e5e5e5] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono uppercase text-xs py-3 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    Next Chapter <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
